import { useState, useMemo } from "react";
import {
  transformEmployeeBenefitsDetail,
  transformPremiumBilling,
  transformEmployerContributionSummary,
} from "@/lib/reportTransformers";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { useQuery } from "@tanstack/react-query";
import { 
  Download, FileText, TrendingUp, Users, BarChart3, Calendar, Clock, Shield, 
  Heart, Building2, DollarSign, UserCheck, FileSpreadsheet, Plus, Play, 
  Star, StarOff, Search, Filter, ChevronRight, Folder, Settings, AlertCircle,
  CheckCircle2, Eye
} from "lucide-react";
import { format } from "date-fns";
import { exportToCSV } from "@/lib/exportUtils";
import { formatDateDisplay } from "@/lib/utils";

interface SystemReport {
  id: string;
  name: string;
  description: string;
  category: "workforce" | "benefits" | "compliance" | "financial" | "time";
  icon: any;
  fields: string[];
  defaultFilters?: Record<string, any>;
}

interface CustomReport {
  id: string;
  name: string;
  description: string;
  createdAt: string;
  lastRun?: string;
  isFavorite: boolean;
  fields: string[];
  filters: Record<string, any>;
}

const SYSTEM_REPORTS: SystemReport[] = [
  {
    id: "employee-census",
    name: "Employee Census",
    description: "Complete list of all employees with demographics and employment details",
    category: "workforce",
    icon: Users,
    fields: ["name", "email", "department", "position", "hireDate", "status", "employmentType", "location"]
  },
  {
    id: "employee-directory",
    name: "Employee Directory",
    description: "Contact information and organizational structure for all employees",
    category: "workforce",
    icon: FileText,
    fields: ["name", "email", "phone", "department", "position", "manager"]
  },
  {
    id: "headcount-by-department",
    name: "Headcount by Department",
    description: "Employee count breakdown by department with trends",
    category: "workforce",
    icon: Building2,
    fields: ["department", "activeCount", "terminatedCount", "newHires", "turnoverRate"]
  },
  {
    id: "new-hires",
    name: "New Hires Report",
    description: "Employees hired within selected date range",
    category: "workforce",
    icon: UserCheck,
    fields: ["name", "department", "position", "hireDate", "status", "manager"]
  },
  {
    id: "terminations",
    name: "Terminations Report",
    description: "Employees terminated within selected date range",
    category: "workforce",
    icon: AlertCircle,
    fields: ["name", "department", "position", "hireDate", "terminationDate", "terminationReason"]
  },
  {
    id: "tenure-analysis",
    name: "Tenure Analysis",
    description: "Employee tenure distribution and average length of service",
    category: "workforce",
    icon: Clock,
    fields: ["name", "department", "hireDate", "yearsOfService", "tenureBand"]
  },
  {
    id: "benefits-enrollment-summary",
    name: "Benefits Enrollment Summary",
    description: "Overview of all benefit plan enrollments by plan type",
    category: "benefits",
    icon: Heart,
    fields: ["planName", "carrier", "planType", "enrolledCount", "waivedCount", "pendingCount", "totalPremium"]
  },
  {
    id: "employee-benefits-detail",
    name: "Employee Benefits Detail",
    description: "Detailed benefits elections for each employee including dependents",
    category: "benefits",
    icon: FileSpreadsheet,
    fields: ["employeeName", "planName", "coverageTier", "effectiveDate", "employeePremium", "employerContribution", "dependents"]
  },
  {
    id: "dependent-coverage",
    name: "Dependent Coverage Report",
    description: "List of all covered dependents with relationship and plan details",
    category: "benefits",
    icon: Users,
    fields: ["employeeName", "dependentName", "relationship", "dateOfBirth", "planName", "effectiveDate"]
  },
  {
    id: "waived-coverage",
    name: "Waived Coverage Report",
    description: "Employees who waived coverage with reasons",
    category: "benefits",
    icon: Shield,
    fields: ["employeeName", "department", "planType", "waiveReason", "waiveDate"]
  },
  {
    id: "premium-billing",
    name: "Premium Billing Report",
    description: "Monthly premium amounts by employee and plan for billing reconciliation",
    category: "financial",
    icon: DollarSign,
    fields: ["employeeName", "planName", "employeePremium", "employerContribution", "totalPremium", "effectiveDate"]
  },
  {
    id: "employer-contribution-summary",
    name: "Employer Contribution Summary",
    description: "Employer contribution totals and percentages by plan and coverage tier",
    category: "financial",
    icon: TrendingUp,
    fields: ["planName", "carrier", "coverageTier", "enrolledCount", "employerContributionPerEmployee", "totalEmployerContribution", "totalPremium", "employerSharePct"]
  },
  {
    id: "aca-eligibility-status",
    name: "ACA Eligibility Status",
    description: "Employee ACA eligibility determination and offer of coverage status",
    category: "compliance",
    icon: Shield,
    fields: ["employeeName", "acaFullTimeMonths", "lnapMonths", "form1095cRequired", "affordability", "firstLine14", "firstLine16"]
  },
  {
    id: "cobra-eligible",
    name: "COBRA Eligible Employees",
    description: "Employees and dependents eligible for COBRA continuation",
    category: "compliance",
    icon: AlertCircle,
    fields: ["employeeName", "qualifyingEvent", "eventDate", "electionDeadline", "cobraStatus"]
  },
  {
    id: "hipaa-training-status",
    name: "HIPAA Training Status",
    description: "Employee HIPAA training completion and certification status",
    category: "compliance",
    icon: CheckCircle2,
    fields: ["employeeName", "trainingCompleted", "completionDate", "expirationDate", "status"]
  },
  {
    id: "pto-balances",
    name: "PTO Balances",
    description: "Current PTO balances and accruals for all employees",
    category: "time",
    icon: Calendar,
    fields: ["employeeName", "department", "policyName", "balance", "accrued", "used", "pending"]
  },
  {
    id: "time-off-usage",
    name: "Time Off Usage",
    description: "Time off requests and usage within selected date range",
    category: "time",
    icon: Clock,
    fields: ["employeeName", "department", "requestType", "startDate", "endDate", "days", "status"]
  }
];

const CATEGORY_LABELS: Record<string, { label: string; icon: any; textClass: string; bgClass: string; iconClass: string }> = {
  workforce: { label: "Workforce", icon: Users, textClass: "text-blue-500", bgClass: "bg-blue-100 dark:bg-blue-900/20", iconClass: "text-blue-600" },
  benefits: { label: "Benefits", icon: Heart, textClass: "text-green-500", bgClass: "bg-green-100 dark:bg-green-900/20", iconClass: "text-green-600" },
  compliance: { label: "Compliance", icon: Shield, textClass: "text-purple-500", bgClass: "bg-purple-100 dark:bg-purple-900/20", iconClass: "text-purple-600" },
  financial: { label: "Financial", icon: DollarSign, textClass: "text-orange-500", bgClass: "bg-orange-100 dark:bg-orange-900/20", iconClass: "text-orange-600" },
  time: { label: "Time & Attendance", icon: Clock, textClass: "text-cyan-500", bgClass: "bg-cyan-100 dark:bg-cyan-900/20", iconClass: "text-cyan-600" }
};

const AVAILABLE_FIELDS = [
  { id: "firstName", label: "First Name", category: "employee" },
  { id: "lastName", label: "Last Name", category: "employee" },
  { id: "email", label: "Email", category: "employee" },
  { id: "phone", label: "Phone", category: "employee" },
  { id: "department", label: "Department", category: "employee" },
  { id: "position", label: "Position/Title", category: "employee" },
  { id: "hireDate", label: "Hire Date", category: "employee" },
  { id: "terminationDate", label: "Termination Date", category: "employee" },
  { id: "status", label: "Employment Status", category: "employee" },
  { id: "employmentType", label: "Employment Type", category: "employee" },
  { id: "manager", label: "Manager", category: "employee" },
  { id: "location", label: "Work Location", category: "employee" },
  { id: "salary", label: "Salary", category: "compensation" },
  { id: "compensationType", label: "Compensation Type", category: "compensation" },
  { id: "payCycle", label: "Pay Cycle", category: "compensation" },
  { id: "planName", label: "Plan Name", category: "benefits" },
  { id: "planType", label: "Plan Type", category: "benefits" },
  { id: "coverageTier", label: "Coverage Tier", category: "benefits" },
  { id: "effectiveDate", label: "Effective Date", category: "benefits" },
  { id: "employeePremium", label: "Employee Premium", category: "benefits" },
  { id: "employerContribution", label: "Employer Contribution", category: "benefits" },
  { id: "employeeContribution", label: "Employee Contribution", category: "benefits" },
  { id: "carrier", label: "Carrier", category: "benefits" },
  { id: "employerContributionPerEmployee", label: "Employer $/Employee", category: "benefits" },
  { id: "totalEmployerContribution", label: "Total Employer Contribution", category: "benefits" },
  { id: "employerSharePct", label: "Employer Share %", category: "benefits" },
  { id: "totalPremium", label: "Total Premium", category: "benefits" },
  { id: "enrollmentStatus", label: "Enrollment Status", category: "benefits" },
  { id: "dependentCount", label: "Dependent Count", category: "benefits" },
  { id: "ptoBalance", label: "PTO Balance", category: "time" },
  { id: "ptoUsed", label: "PTO Used", category: "time" },
  { id: "ptoAccrued", label: "PTO Accrued", category: "time" }
];

export default function Reports() {
  const { user } = useAuth();
  const employerClientId = user?.clientId != null ? String(user.clientId) : "";

  const [activeTab, setActiveTab] = useState("system");
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [selectedReport, setSelectedReport] = useState<SystemReport | null>(null);
  const [isRunningReport, setIsRunningReport] = useState(false);
  const [reportData, setReportData] = useState<any[]>([]);
  const [showReportPreview, setShowReportPreview] = useState(false);

  const [isCustomReportOpen, setIsCustomReportOpen] = useState(false);
  const [customReportName, setCustomReportName] = useState("");
  const [customReportDescription, setCustomReportDescription] = useState("");
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [savedCustomReports, setSavedCustomReports] = useState<CustomReport[]>([]);
  const [dateRangeStart, setDateRangeStart] = useState("");
  const [dateRangeEnd, setDateRangeEnd] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const { data: employees = [] } = useQuery<any[]>({
    queryKey: [`/api/clients/${employerClientId}/employees`],
    enabled: !!employerClientId,
  });

  const { data: enrollments = [] } = useQuery<any[]>({
    queryKey: ["/api/enrollments", { clientId: employerClientId }],
    enabled: !!employerClientId,
  });

  const { data: dependents = [] } = useQuery<any[]>({
    queryKey: ["/api/dependents", { clientId: employerClientId }],
    enabled: !!employerClientId,
  });

  // ACA figures come from the engine snapshot for the last complete tax year.
  const acaReportYear = new Date().getFullYear() - 1;
  const { data: acaYear } = useQuery<{ result?: { employees: any[] } }>({
    queryKey: ["/api/aca/year", employerClientId, acaReportYear],
    queryFn: async () => {
      const res = await fetch(`/api/aca/year?clientId=${employerClientId}&taxYear=${acaReportYear}`, { credentials: "include" });
      if (!res.ok) return {};
      return res.json();
    },
    enabled: !!employerClientId,
  });

  const { data: plans = [] } = useQuery<any[]>({
    queryKey: ["/api/plans"],
  });

  // Build plan lookup by ID for enriching enrollment data
  const planMap = useMemo(() => {
    const map = new Map<number, any>();
    plans.forEach((p: any) => map.set(p.id, p));
    return map;
  }, [plans]);

  const getPlanName = (enr: any) => planMap.get(enr.planId)?.name || `Plan ${enr.planId}`;
  const getPlanType = (enr: any) => planMap.get(enr.planId)?.type || "-";
  const getPlanCarrier = (enr: any) => planMap.get(enr.planId)?.carrier || "-";

  const activeEmployees = employees.filter((emp: any) => emp.status === "active");

  const filteredReports = useMemo(() => {
    return SYSTEM_REPORTS.filter(report => {
      const matchesSearch = report.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        report.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = categoryFilter === "all" || report.category === categoryFilter;
      return matchesSearch && matchesCategory;
    });
  }, [searchQuery, categoryFilter]);

  const reportsByCategory = useMemo(() => {
    const grouped: Record<string, SystemReport[]> = {};
    filteredReports.forEach(report => {
      if (!grouped[report.category]) {
        grouped[report.category] = [];
      }
      grouped[report.category].push(report);
    });
    return grouped;
  }, [filteredReports]);

  const runReport = async (report: SystemReport) => {
    setIsRunningReport(true);
    setSelectedReport(report);
    
    await new Promise(resolve => setTimeout(resolve, 500));

    let data: any[] = [];

    switch (report.id) {
      case "employee-census":
      case "employee-directory":
        data = employees.map((emp: any) => ({
          name: `${emp.firstName} ${emp.lastName}`,
          email: emp.email,
          phone: emp.phone || "-",
          department: emp.department || "-",
          position: emp.position || "-",
          hireDate: emp.hireDate ? formatDateDisplay(emp.hireDate) : "-",
          status: emp.status || "active",
          employmentType: emp.employmentType || "-",
          location: emp.city && emp.state ? `${emp.city}, ${emp.state}` : "-",
          manager: employees.find((m: any) => m.id === emp.managerEmployeeId)
            ? `${employees.find((m: any) => m.id === emp.managerEmployeeId).firstName} ${employees.find((m: any) => m.id === emp.managerEmployeeId).lastName}`
            : "-"
        }));
        break;

      case "headcount-by-department":
        const depts = Array.from(new Set(employees.map((e: any) => e.department || "Unassigned")));
        data = depts.map(dept => {
          const deptEmps = employees.filter((e: any) => (e.department || "Unassigned") === dept);
          const active = deptEmps.filter((e: any) => e.status === "active").length;
          const terminated = deptEmps.filter((e: any) => e.status === "terminated").length;
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
          const newHires = deptEmps.filter((e: any) => e.hireDate && new Date(e.hireDate) >= thirtyDaysAgo).length;
          return {
            department: dept,
            activeCount: active,
            terminatedCount: terminated,
            newHires,
            turnoverRate: deptEmps.length > 0 ? `${Math.round((terminated / deptEmps.length) * 100)}%` : "0%"
          };
        });
        break;

      case "new-hires":
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        data = employees
          .filter((emp: any) => emp.hireDate && new Date(emp.hireDate) >= thirtyDaysAgo)
          .map((emp: any) => ({
            name: `${emp.firstName} ${emp.lastName}`,
            department: emp.department || "-",
            position: emp.position || "-",
            hireDate: emp.hireDate ? formatDateDisplay(emp.hireDate) : "-",
            status: emp.status || "active",
            manager: employees.find((m: any) => m.id === emp.managerEmployeeId)
              ? `${employees.find((m: any) => m.id === emp.managerEmployeeId).firstName} ${employees.find((m: any) => m.id === emp.managerEmployeeId).lastName}`
              : "-"
          }));
        break;

      case "terminations":
        data = employees
          .filter((emp: any) => emp.status === "terminated")
          .map((emp: any) => ({
            name: `${emp.firstName} ${emp.lastName}`,
            department: emp.department || "-",
            position: emp.position || "-",
            hireDate: emp.hireDate ? formatDateDisplay(emp.hireDate) : "-",
            terminationDate: emp.terminationDate ? formatDateDisplay(emp.terminationDate) : "-",
            terminationReason: emp.terminationReason || "-"
          }));
        break;

      case "tenure-analysis":
        data = activeEmployees.map((emp: any) => {
          const hireDate = emp.hireDate ? new Date(emp.hireDate) : null;
          const years = hireDate ? Math.floor((Date.now() - hireDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000)) : 0;
          let tenureBand = "< 1 year";
          if (years >= 10) tenureBand = "10+ years";
          else if (years >= 5) tenureBand = "5-10 years";
          else if (years >= 3) tenureBand = "3-5 years";
          else if (years >= 1) tenureBand = "1-3 years";
          return {
            name: `${emp.firstName} ${emp.lastName}`,
            department: emp.department || "-",
            hireDate: emp.hireDate ? formatDateDisplay(emp.hireDate) : "-",
            yearsOfService: years,
            tenureBand
          };
        });
        break;

      case "benefits-enrollment-summary":
        const planEnrollments: Record<string, any> = {};
        enrollments.forEach((enr: any) => {
          const key = String(enr.planId);
          if (!planEnrollments[key]) {
            planEnrollments[key] = {
              planName: getPlanName(enr),
              planType: getPlanType(enr),
              carrier: getPlanCarrier(enr),
              enrolledCount: 0,
              waivedCount: 0,
              pendingCount: 0,
              totalPremium: 0
            };
          }
          if (enr.waivedCoverage) {
            planEnrollments[key].waivedCount++;
          } else if (enr.status === "pending") {
            planEnrollments[key].pendingCount++;
          } else {
            planEnrollments[key].enrolledCount++;
            planEnrollments[key].totalPremium += Number(enr.totalPremium || 0);
          }
        });
        data = Object.values(planEnrollments).map((p: any) => ({
          ...p,
          totalPremium: `$${p.totalPremium.toLocaleString()}`
        }));
        break;

      case "employee-benefits-detail":
        data = transformEmployeeBenefitsDetail(enrollments, employees, dependents, planMap, formatDateDisplay);
        break;

      case "dependent-coverage":
        data = dependents.map((dep: any) => {
          const emp = employees.find((e: any) => e.id === dep.employeeId);
          const enr = enrollments.find((e: any) => e.employeeId === dep.employeeId && e.status === "active");
          return {
            employeeName: emp ? `${emp.firstName} ${emp.lastName}` : "-",
            dependentName: `${dep.firstName} ${dep.lastName}`,
            relationship: dep.relationship || "-",
            dateOfBirth: dep.dateOfBirth ? formatDateDisplay(dep.dateOfBirth) : "-",
            planName: enr ? getPlanName(enr) : "-",
            effectiveDate: enr?.effectiveDate ? formatDateDisplay(enr.effectiveDate) : "-"
          };
        });
        break;

      case "waived-coverage":
        data = enrollments
          .filter((enr: any) => enr.waivedCoverage)
          .map((enr: any) => {
            const emp = employees.find((e: any) => e.id === enr.employeeId);
            return {
              employeeName: emp ? `${emp.firstName} ${emp.lastName}` : "-",
              department: emp?.department || "-",
              planType: getPlanType(enr),
              waiveReason: enr.waiverReason || "-",
              waiveDate: enr.effectiveDate ? formatDateDisplay(enr.effectiveDate) : "-"
            };
          });
        break;

      case "premium-billing":
        data = transformPremiumBilling(enrollments, employees, planMap, formatDateDisplay);
        break;

      case "employer-contribution-summary":
        data = transformEmployerContributionSummary(enrollments, planMap);
        break;

      case "aca-eligibility-status":
        data = (acaYear?.result?.employees ?? []).map((e: any) => {
          const months: any[] = e.months ?? [];
          const first14 = months.find((m) => m.line14)?.line14 ?? "-";
          const first16 = months.find((m) => m.line16)?.line16 ?? "-";
          const affordability: Record<string, string> = { affordable: "Affordable", not_affordable: "Not Affordable", pending_data: "Pending Data", not_applicable: "Not Applicable", mixed: "Mixed" };
          return {
            employeeName: `${e.firstName} ${e.lastName}`,
            acaFullTimeMonths: `${e.summary?.fullTimeMonths ?? 0} of 12`,
            lnapMonths: String(e.summary?.lnapMonths ?? 0),
            form1095cRequired: e.formRequired === "full_time" ? "Yes (full-time)" : e.formRequired === "self_insured_enrolled" ? "Yes (self-insured enrollee)" : "No",
            affordability: affordability[e.summary?.status] ?? "-",
            firstLine14: first14,
            firstLine16: first16,
          };
        });
        break;

      case "cobra-eligible":
        data = employees
          .filter((emp: any) => emp.status === "terminated" && emp.terminationDate)
          .map((emp: any) => {
            const termDate = new Date(emp.terminationDate);
            const electionDeadline = new Date(termDate);
            electionDeadline.setDate(electionDeadline.getDate() + 60);
            return {
              employeeName: `${emp.firstName} ${emp.lastName}`,
              qualifyingEvent: "Termination",
              eventDate: format(termDate, "MM/dd/yyyy"),
              electionDeadline: format(electionDeadline, "MM/dd/yyyy"),
              cobraStatus: electionDeadline > new Date() ? "Election Period Open" : "Election Period Expired"
            };
          });
        break;

      case "hipaa-training-status":
        data = activeEmployees.map((emp: any) => ({
          employeeName: `${emp.firstName} ${emp.lastName}`,
          trainingCompleted: "Pending",
          completionDate: "-",
          expirationDate: "-",
          status: "Incomplete"
        }));
        break;

      case "pto-balances":
        data = activeEmployees.map((emp: any) => ({
          employeeName: `${emp.firstName} ${emp.lastName}`,
          department: emp.department || "-",
          policyName: "Standard PTO",
          balance: "N/A",
          accrued: "N/A",
          used: "N/A",
          pending: "N/A"
        }));
        break;

      case "time-off-usage":
        data = [{
          employeeName: "No time off data available",
          department: "-",
          requestType: "-",
          startDate: "-",
          endDate: "-",
          days: "-",
          status: "-"
        }];
        break;

      default:
        data = [{ message: "Report generation requires additional data sources. Contact administrator." }];
    }

    setReportData(data);
    setShowReportPreview(true);
    setIsRunningReport(false);
  };

  const exportReport = () => {
    if (!selectedReport || reportData.length === 0) return;
    exportToCSV(reportData, { filename: `${selectedReport.id}-${format(new Date(), "yyyy-MM-dd")}` });
  };

  const toggleField = (fieldId: string) => {
    setSelectedFields(prev => 
      prev.includes(fieldId) 
        ? prev.filter(f => f !== fieldId)
        : [...prev, fieldId]
    );
  };

  const saveCustomReport = () => {
    if (!customReportName.trim() || selectedFields.length === 0) return;
    
    const newReport: CustomReport = {
      id: `custom-${Date.now()}`,
      name: customReportName,
      description: customReportDescription,
      createdAt: new Date().toISOString(),
      isFavorite: false,
      fields: selectedFields,
      filters: {
        dateRangeStart,
        dateRangeEnd,
        statusFilter
      }
    };
    
    setSavedCustomReports(prev => [...prev, newReport]);
    setIsCustomReportOpen(false);
    setCustomReportName("");
    setCustomReportDescription("");
    setSelectedFields([]);
  };

  const toggleFavorite = (reportId: string) => {
    setSavedCustomReports(prev => 
      prev.map(r => r.id === reportId ? { ...r, isFavorite: !r.isFavorite } : r)
    );
  };

  const runCustomReport = (customReport: CustomReport) => {
    setIsRunningReport(true);
    
    let filteredData = employees.filter((emp: any) => {
      if (customReport.filters.statusFilter && customReport.filters.statusFilter !== "all") {
        if (emp.status !== customReport.filters.statusFilter) return false;
      }
      if (customReport.filters.dateRangeStart && emp.hireDate) {
        if (new Date(emp.hireDate) < new Date(customReport.filters.dateRangeStart)) return false;
      }
      if (customReport.filters.dateRangeEnd && emp.hireDate) {
        if (new Date(emp.hireDate) > new Date(customReport.filters.dateRangeEnd)) return false;
      }
      return true;
    });

    const fieldMappings: Record<string, (emp: any) => any> = {
      firstName: (emp) => emp.firstName,
      lastName: (emp) => emp.lastName,
      email: (emp) => emp.email,
      phone: (emp) => emp.phone || "-",
      department: (emp) => emp.department || "-",
      position: (emp) => emp.position || "-",
      hireDate: (emp) => emp.hireDate ? formatDateDisplay(emp.hireDate) : "-",
      terminationDate: (emp) => emp.terminationDate ? formatDateDisplay(emp.terminationDate) : "-",
      status: (emp) => emp.status || "active",
      employmentType: (emp) => emp.employmentType || "-",
      manager: (emp) => {
        const mgr = employees.find((m: any) => m.id === emp.managerEmployeeId);
        return mgr ? `${mgr.firstName} ${mgr.lastName}` : "-";
      },
      location: (emp) => emp.city && emp.state ? `${emp.city}, ${emp.state}` : "-",
      salary: (emp) => emp.salary ? `$${Number(emp.salary).toLocaleString()}` : "-",
      compensationType: (emp) => emp.compensationType || "-",
      payCycle: (emp) => emp.payCycle || "-",
      planName: (emp) => {
        const enr = enrollments.find((e: any) => e.employeeId === emp.id && e.status === "active");
        return enr ? getPlanName(enr) : "-";
      },
      planType: (emp) => {
        const enr = enrollments.find((e: any) => e.employeeId === emp.id && e.status === "active");
        return enr ? getPlanType(enr) : "-";
      },
      coverageTier: (emp) => {
        const enr = enrollments.find((e: any) => e.employeeId === emp.id && e.status === "active");
        return enr?.coverageTier || "-";
      },
      effectiveDate: (emp) => {
        const enr = enrollments.find((e: any) => e.employeeId === emp.id && e.status === "active");
        return enr?.effectiveDate ? formatDateDisplay(enr.effectiveDate) : "-";
      },
      employeePremium: (emp) => {
        const enr = enrollments.find((e: any) => e.employeeId === emp.id && e.status === "active");
        return enr ? `$${Number(enr.employeePremium || 0).toFixed(2)}` : "-";
      },
      employerContribution: (emp) => {
        const enr = enrollments.find((e: any) => e.employeeId === emp.id && e.status === "active");
        return enr ? `$${Number(enr.employerPremium || 0).toFixed(2)}` : "-";
      },
      employeeContribution: (emp) => {
        const enr = enrollments.find((e: any) => e.employeeId === emp.id && e.status === "active");
        return enr ? `$${Number(enr.employeePremium || 0).toFixed(2)}` : "-";
      },
      carrier: (emp) => {
        const enr = enrollments.find((e: any) => e.employeeId === emp.id && e.status === "active");
        return enr ? getPlanCarrier(enr) : "-";
      },
      totalPremium: (emp) => {
        const enr = enrollments.find((e: any) => e.employeeId === emp.id && e.status === "active");
        return enr ? `$${Number(enr.totalPremium || 0).toFixed(2)}` : "-";
      },
      enrollmentStatus: (emp) => {
        const enr = enrollments.find((e: any) => e.employeeId === emp.id);
        return enr ? enr.status : "Not Enrolled";
      },
      dependentCount: (emp) => {
        return dependents.filter((d: any) => d.employeeId === emp.id).length;
      },
      ptoBalance: () => "N/A",
      ptoUsed: () => "N/A",
      ptoAccrued: () => "N/A"
    };

    const data = filteredData.map((emp: any) => {
      const row: Record<string, any> = {};
      customReport.fields.forEach(fieldId => {
        const fieldDef = AVAILABLE_FIELDS.find(f => f.id === fieldId);
        if (fieldDef && fieldMappings[fieldId]) {
          row[fieldDef.label] = fieldMappings[fieldId](emp);
        }
      });
      return row;
    });

    setSavedCustomReports(prev => 
      prev.map(r => r.id === customReport.id ? { ...r, lastRun: new Date().toISOString() } : r)
    );
    
    setSelectedReport({ 
      id: customReport.id, 
      name: customReport.name, 
      description: customReport.description,
      category: "workforce",
      icon: FileSpreadsheet,
      fields: customReport.fields
    });
    setReportData(data);
    setShowReportPreview(true);
    setIsRunningReport(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Reports</h1>
          <p className="text-muted-foreground">
            Generate standard reports or create custom reports for your organization
          </p>
        </div>
        <Dialog open={isCustomReportOpen} onOpenChange={setIsCustomReportOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Create Custom Report
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle>Create Custom Report</DialogTitle>
              <DialogDescription>
                Build a custom report by selecting the fields and filters you need
              </DialogDescription>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto space-y-6 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="report-name">Report Name</Label>
                  <Input
                    id="report-name"
                    placeholder="e.g., Monthly Headcount Report"
                    value={customReportName}
                    onChange={(e) => setCustomReportName(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="report-description">Description (Optional)</Label>
                  <Input
                    id="report-description"
                    placeholder="Brief description of this report"
                    value={customReportDescription}
                    onChange={(e) => setCustomReportDescription(e.target.value)}
                  />
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">Select Fields</Label>
                  <span className="text-sm text-muted-foreground">
                    {selectedFields.length} fields selected
                  </span>
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  {["employee", "compensation", "benefits", "time"].map(cat => (
                    <Card key={cat} className="p-3">
                      <h4 className="font-medium capitalize mb-2">{cat}</h4>
                      <div className="space-y-2">
                        {AVAILABLE_FIELDS
                          .filter(f => f.category === cat)
                          .map(field => (
                            <label key={field.id} className="flex items-center gap-2 text-sm cursor-pointer">
                              <Checkbox
                                checked={selectedFields.includes(field.id)}
                                onCheckedChange={() => toggleField(field.id)}
                              />
                              {field.label}
                            </label>
                          ))
                        }
                      </div>
                    </Card>
                  ))}
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <Label className="text-base font-semibold">Filters</Label>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="date-start">Date Range Start</Label>
                    <Input
                      id="date-start"
                      type="date"
                      value={dateRangeStart}
                      onChange={(e) => setDateRangeStart(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="date-end">Date Range End</Label>
                    <Input
                      id="date-end"
                      type="date"
                      value={dateRangeEnd}
                      onChange={(e) => setDateRangeEnd(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="status-filter">Status Filter</Label>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger>
                        <SelectValue placeholder="All statuses" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Statuses</SelectItem>
                        <SelectItem value="active">Active Only</SelectItem>
                        <SelectItem value="terminated">Terminated Only</SelectItem>
                        <SelectItem value="on-leave">On Leave</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCustomReportOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={saveCustomReport}
                disabled={!customReportName.trim() || selectedFields.length === 0}
              >
                Save Report
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">System Reports</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{SYSTEM_REPORTS.length}</div>
            <p className="text-xs text-muted-foreground">Available reports</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">My Custom Reports</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{savedCustomReports.length}</div>
            <p className="text-xs text-muted-foreground">Saved reports</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Active Employees</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeEmployees.length}</div>
            <p className="text-xs text-muted-foreground">Available for reporting</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Data Categories</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">5</div>
            <p className="text-xs text-muted-foreground">Workforce, Benefits, Compliance, Financial, Time</p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="system">
            <Folder className="h-4 w-4 mr-2" />
            System Reports
          </TabsTrigger>
          <TabsTrigger value="custom">
            <Settings className="h-4 w-4 mr-2" />
            My Reports
          </TabsTrigger>
        </TabsList>

        <TabsContent value="system" className="space-y-6">
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search reports..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-48">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {Object.entries(CATEGORY_LABELS).map(([key, { label }]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {Object.entries(reportsByCategory).map(([category, reports]) => {
            const categoryInfo = CATEGORY_LABELS[category];
            const Icon = categoryInfo.icon;
            return (
              <Card key={category}>
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <Icon className={`h-5 w-5 ${categoryInfo.textClass}`} />
                    <CardTitle className="text-lg">{categoryInfo.label} Reports</CardTitle>
                    <Badge variant="secondary">{reports.length}</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {reports.map(report => {
                      const ReportIcon = report.icon;
                      return (
                        <div
                          key={report.id}
                          className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors group"
                        >
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${categoryInfo.bgClass}`}>
                              <ReportIcon className={`h-4 w-4 ${categoryInfo.iconClass}`} />
                            </div>
                            <div>
                              <p className="font-medium text-sm">{report.name}</p>
                              <p className="text-xs text-muted-foreground line-clamp-1">
                                {report.description}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => runReport(report)}
                              disabled={isRunningReport}
                            >
                              <Eye className="h-3 w-3 mr-1" />
                              Run
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>

        <TabsContent value="custom" className="space-y-4">
          {savedCustomReports.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <FileSpreadsheet className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Custom Reports Yet</h3>
                <p className="text-muted-foreground text-center mb-4">
                  Create your first custom report to save frequently used report configurations
                </p>
                <Button onClick={() => setIsCustomReportOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Custom Report
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {savedCustomReports.map(report => (
                <Card key={report.id} className="hover:border-primary/50 transition-colors">
                  <CardContent className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-4">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => toggleFavorite(report.id)}
                      >
                        {report.isFavorite ? (
                          <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                        ) : (
                          <StarOff className="h-4 w-4 text-muted-foreground" />
                        )}
                      </Button>
                      <div>
                        <p className="font-medium">{report.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {report.description || `${report.fields.length} fields selected`}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Created {format(new Date(report.createdAt), "MMM d, yyyy")}
                          {report.lastRun && ` • Last run ${format(new Date(report.lastRun), "MMM d, yyyy")}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => runCustomReport(report)}
                        disabled={isRunningReport}
                      >
                        <Play className="h-3 w-3 mr-1" />
                        Run
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={showReportPreview} onOpenChange={setShowReportPreview}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedReport?.icon && <selectedReport.icon className="h-5 w-5" />}
              {selectedReport?.name}
            </DialogTitle>
            <DialogDescription>
              {reportData.length} records found • Generated on {format(new Date(), "MMM d, yyyy 'at' h:mm a")}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-auto">
            {reportData.length > 0 ? (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted">
                    <tr>
                      {Object.keys(reportData[0]).map(key => (
                        <th key={key} className="px-4 py-2 text-left font-medium capitalize">
                          {key.replace(/([A-Z])/g, ' $1').trim()}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.slice(0, 100).map((row, idx) => (
                      <tr key={idx} className="border-t hover:bg-muted/50">
                        {Object.values(row).map((val: any, i) => (
                          <td key={i} className="px-4 py-2">
                            {typeof val === "object" ? JSON.stringify(val) : String(val)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {reportData.length > 100 && (
                  <div className="p-3 text-center text-muted-foreground text-sm bg-muted/50">
                    Showing first 100 of {reportData.length} records. Export to see all data.
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12">
                <FileText className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No data available for this report</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReportPreview(false)}>
              Close
            </Button>
            <Button onClick={exportReport} disabled={reportData.length === 0}>
              <Download className="h-4 w-4 mr-2" />
              Export to CSV
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
